import React from "react";
import { Link } from "react-router-dom";
import { Flower2, Frame, Type, Hammer, Phone, ArrowRight } from "lucide-react";
import { SITE, IMAGES } from "@/lib/site";
import SectionTitle from "@/components/SectionTitle";
import PriceBanner from "@/components/PriceBanner";
import usePageMeta from "@/hooks/usePageMeta";

const PRESTATIONS = [
  { icon: Hammer, title: "Monuments funéraires", desc: "Création, pose et rénovation de tombes et caveaux. Granits français, marbres nobles, formes traditionnelles ou contemporaines." },
  { icon: Type, title: "Gravures personnalisées", desc: "Lettres dorées, argentées, peintes ou émaillées. Symboles religieux, motifs personnalisés, portraits." },
  { icon: Frame, title: "Plaques & médaillons", desc: "Plaques en granit, marbre, verre ou bronze. Médaillons photo céramique pour préserver le souvenir." },
  { icon: Flower2, title: "Compositions florales", desc: "Bouquets, gerbes, couronnes, raquettes : fleurs naturelles ou stabilisées, arrangements sur mesure." },
];

const URNES = [
  "Urnes en pierre naturelle (marbre, granit)",
  "Urnes biodégradables pour dispersion",
  "Urnes décoratives — bois, céramique, verre",
  "Urnes de cendres familiales et personnalisées",
];

export default function MarbreriePage() {
  usePageMeta(
    "Marbrerie funéraire & Ornements — Baho",
    "Monuments funéraires, gravures personnalisées, plaques, médaillons, urnes et compositions florales. Travail soigné dans les Pyrénées-Orientales."
  );
  return (
    <div data-testid="marbrerie-page">
      <section className="relative bg-black text-white py-28 lg:py-40">
        <div className="absolute inset-0 opacity-25">
          <img src={IMAGES.lily} alt="" className="w-full h-full object-cover grayscale" />
        </div>
        <div className="relative container-soft">
          <p className="overline text-white/60">Marbrerie & Ornements</p>
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-4 max-w-4xl tracking-tight leading-[1.02]">
            Un dernier hommage <em className="italic font-light">à la hauteur du souvenir</em>.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg leading-relaxed">
            Tombes, plaques, gravures, urnes et fleurs. Notre marbrerie travaille
            la pierre et le souvenir avec la même rigueur — du dessin à la pose.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="container-soft">
          <SectionTitle
            overline="Nos prestations"
            title="Quatre savoir-faire, une seule exigence : la dignité."
          />
          <div className="grid md:grid-cols-2 gap-px bg-black/10 mt-16 border border-black/10">
            {PRESTATIONS.map((p, i) => {
              const Icon = p.icon;
              return (
                <article
                  key={i}
                  data-testid={`marbrerie-presta-${i}`}
                  className="bg-white p-10 lg:p-12 hover:bg-neutral-50 transition-colors duration-500"
                >
                  <Icon size={32} strokeWidth={1.25} />
                  <h3 className="font-serif-display text-2xl lg:text-3xl mt-6">{p.title}</h3>
                  <p className="mt-4 text-neutral-600 leading-relaxed text-lg">{p.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-neutral-50 py-24 lg:py-32 border-y border-black/10">
        <div className="container-soft grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img src={IMAGES.lily} alt="Lys blanc" className="w-full aspect-square object-cover grayscale" loading="lazy" />
          </div>
          <div className="lg:col-span-6">
            <p className="overline">Urnes funéraires</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
              Choisir l'urne juste, sans précipitation.
            </h2>
            <p className="mt-6 text-neutral-700 text-lg leading-relaxed">
              Nous présentons un large choix d'urnes — des plus sobres aux plus personnalisées —
              et vous accompagnons dans le choix d'un objet qui vous ressemble.
            </p>
            <ul className="mt-8 space-y-3">
              {URNES.map((u, i) => (
                <li key={i} className="flex items-start gap-3 pb-3 border-b border-black/10">
                  <span className="font-serif-display text-black/40">—</span>
                  <span className="text-neutral-800">{u}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="container-soft">
          <SectionTitle
            overline="Entretien & rénovation"
            title="Pour que la mémoire reste belle, année après année."
            kicker="Nettoyage de sépultures, redorure des gravures, remplacement des plaques, fleurissement régulier : nous prenons soin du lieu de recueillement aussi longtemps que vous le souhaitez."
          />
        </div>
      </section>

      <PriceBanner />

      <section className="py-24 lg:py-32 text-center">
        <div className="container-soft">
          <SectionTitle
            overline="Un projet en marbrerie ?"
            title="Recevez un devis détaillé, sans engagement."
            align="center"
          />
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={SITE.phoneHref} data-testid="marbrerie-cta-call" className="inline-flex items-center justify-center gap-3 bg-black text-white px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-neutral-800 transition-all duration-300">
              <Phone size={18} strokeWidth={1.5} /> Nous appeler
            </a>
            <Link to="/contact" data-testid="marbrerie-cta-contact" className="inline-flex items-center justify-center gap-2 border border-black px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-black hover:text-white transition-all duration-300">
              Devis gratuit <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
